import { addDays, addWorkingDays, startOfDay } from "./workingDays";

export const ACTIVE_REFUND_STATUSES = [
  "Processing refund",
  "Refund Offer Pending",
  "Return Required",
  "Return in Transit",
  "Refund Awaiting Seller Confirmation",
];

export function isVaMediationInProgress(orderOrStatus) {
  const status =
    typeof orderOrStatus === "string" ? orderOrStatus : orderOrStatus?.vaMediation?.status;
  return status === "open" || status === "mediating";
}

/** Refund case still in progress (seller/buyer actions may apply). */
export const CLOSED_REFUND_STATUSES = [
  "Refund Success",
  "Refund Rejected",
  "Refund Resolved",
];

export function orderPartialRefundSettled(order) {
  if (!order) return false;
  if (order.partialRefundAcceptedAt) return true;
  if (Number(order.partialRefundAcceptedAmount) > 0) return true;
  const refunds = Array.isArray(order.refunds) ? order.refunds : [];
  return refunds.some((r) => r.status === "Refund Success" && r.partialRefundAccepted);
}

export function orderHasActiveRefundCase(order) {
  if (!order) return false;
  if (orderPartialRefundSettled(order)) return false;
  const refunds = Array.isArray(order.refunds) ? order.refunds : [];
  if (refunds.some((r) => ACTIVE_REFUND_STATUSES.includes(r.status))) return true;
  if (ACTIVE_REFUND_STATUSES.includes(order.status || "")) return true;
  if (
    order.buyerReportedIssueAt &&
    !order.payoutReleased &&
    !order.userReceivedConfirmed &&
    order.vaMediation?.status !== "resolved"
  ) {
    return true;
  }
  return false;
}

/** Formal refund request in progress (excludes issue-only — buyer may still submit refund details). */
export function orderRefundRequestInProgress(order) {
  if (!order) return false;
  if (orderPartialRefundSettled(order)) return false;
  // Completed orders are closed for new refund/VA actions.
  if (order.userReceivedConfirmed || order.payoutReleased) {
    return isVaMediationInProgress(order);
  }
  if (isVaMediationInProgress(order)) return true;
  const refunds = Array.isArray(order.refunds) ? order.refunds : [];
  if (refunds.some((r) => ACTIVE_REFUND_STATUSES.includes(r.status))) return true;
  if (ACTIVE_REFUND_STATUSES.includes(order.status || "")) return true;
  if (order.refundRequestedAt && order.status !== "Refund Resolved") return true;
  return false;
}

export function orderIssueReportedOnly(order) {
  if (!order?.buyerReportedIssueAt || order.payoutReleased || order.userReceivedConfirmed) {
    return false;
  }
  if (order.vaMediation?.status === "resolved") return false;
  return !orderRefundRequestInProgress(order);
}

export function orderRefundCaseIsClosed(order) {
  if (!order) return false;
  if (orderPartialRefundSettled(order)) return true;
  const effective = getEffectiveRefundAwareStatus(order);
  if (CLOSED_REFUND_STATUSES.includes(effective)) return true;
  if (CLOSED_REFUND_STATUSES.includes(order.status || "")) return true;
  const refunds = Array.isArray(order.refunds) ? order.refunds : [];
  if (refunds.length > 0 && refunds.every((r) => CLOSED_REFUND_STATUSES.includes(r.status))) {
    return true;
  }
  if (String(order.refundReason || "").trim() && order.status === "Refund Resolved") {
    return true;
  }
  return false;
}

/** Product line already has any refund record (open or closed). */
export function productLineHasRefundRecord(order, productId) {
  if (!order || productId == null) return false;
  const refunds = Array.isArray(order.refunds) ? order.refunds : [];
  return refunds.some((r) => String(r.productId) === String(productId));
}

/** Refund-aware base status (matches order list / detail aggregation). */
export function getEffectiveRefundAwareStatus(order) {
  if (orderPartialRefundSettled(order)) return "Refund Success";
  if (!order?.refunds?.length) return order?.status || "";

  const allRejected = order.refunds.every((r) => r.status === "Refund Rejected");
  const allSuccess = order.refunds.every((r) => r.status === "Refund Success");
  const allResolved = order.refunds.every((r) => r.status === "Refund Resolved");
  const hasOfferPending = order.refunds.some((r) => r.status === "Refund Offer Pending");

  if (allRejected) return "Refund Rejected";
  if (allSuccess) return "Refund Success";
  if (allResolved) return "Refund Resolved";
  if (hasOfferPending) return "Refund Offer Pending";
  return order.status || "";
}

export const BUYER_DISPUTE_LIST_STATUSES = [
  "Processing refund",
  "Refund Offer Pending",
  "Return Required",
  "Return in Transit",
  "Refund Awaiting Seller Confirmation",
  "Refund Rejected",
  "Refund Success",
  "Refund Resolved",
];

/**
 * Orders that should appear on Profile → Disputes & Refunds → My Refunds.
 * Includes product-level refund lines and VA mediation, not only order.status.
 */
export function orderBelongsInBuyerDisputesList(order) {
  if (!order) return false;

  if (isVaMediationInProgress(order) || order.vaMediation?.status === "resolved") {
    return true;
  }

  if (BUYER_DISPUTE_LIST_STATUSES.includes(order.status || "")) return true;
  if (BUYER_DISPUTE_LIST_STATUSES.includes(getEffectiveRefundAwareStatus(order))) return true;

  const refunds = Array.isArray(order.refunds) ? order.refunds : [];
  if (refunds.some((r) => BUYER_DISPUTE_LIST_STATUSES.includes(r.status))) return true;

  if (order.refundRequestedAt) return true;
  if (String(order.refundReason || "").trim()) return true;

  if (
    order.buyerReportedIssueAt &&
    !order.payoutReleased &&
    !order.userReceivedConfirmed
  ) {
    return true;
  }

  return false;
}

/** Orders that should appear on seller Buyer Refunds list. */
export function orderBelongsInSellerDisputesList(order) {
  if (!order) return false;
  if (isVaMediationInProgress(order) || order.vaMediation?.status === "resolved") {
    return true;
  }
  if (BUYER_DISPUTE_LIST_STATUSES.includes(order.status || "")) return true;
  if (BUYER_DISPUTE_LIST_STATUSES.includes(getEffectiveRefundAwareStatus(order))) return true;
  const refunds = Array.isArray(order.refunds) ? order.refunds : [];
  if (refunds.some((r) => BUYER_DISPUTE_LIST_STATUSES.includes(r.status))) return true;
  if (order.refundRequestedAt) return true;
  return false;
}

export function isOrderCompleted(order) {
  return Boolean(order?.userReceivedConfirmed || order?.payoutReleased);
}

function endOfDay(dateLike) {
  const d = new Date(dateLike);
  d.setHours(23, 59, 59, 999);
  return d;
}

/**
 * UI safety net:
 * If backend auto-release/status update is delayed, treat orders as completed
 * once the buyer confirmation window has ended (estimated delivery + 2 days).
 */
function confirmationWindowEnded(order) {
  if (!order) return false;
  if (orderHasActiveRefundCase(order)) return false;
  if (order.buyerReportedIssueAt && !order.payoutReleased && !order.userReceivedConfirmed) {
    return false;
  }
  if (isOrderCompleted(order)) return true;

  const maxDays = Number(order?.estimatedDeliveryMaxDays ?? order?.estimatedDeliveryDays);
  if (!Number.isFinite(maxDays) || maxDays < 1) return false;

  if (!order?.dispatchedAt) return false;

  const referenceDateRaw = order.dispatchedAt;

  const estimatedDeliveryDate = addWorkingDays(startOfDay(referenceDateRaw), maxDays);
  const autoReleaseAt = endOfDay(addDays(estimatedDeliveryDate, 2));
  return Date.now() > autoReleaseAt.getTime();
}

const STATUS_LABELS = {
  "Issue reported": "Issue reported",
  "Refund Success": "Approved",
  "Refund Rejected": "Declined",
  "Refund Resolved": "Resolved (no refund)",
  "Processing refund": "Under review",
  "Refund Offer Pending": "Partial refund offered",
  "Return Required": "Return required",
  "Return in Transit": "Return in transit",
  "Refund Awaiting Seller Confirmation": "Awaiting seller confirmation",
};

/** Human-readable label for orders table / tabs. */
export function getOrderListStatusLabel(order) {
  const rawStatus = String(order?.status || "").toLowerCase();
  if (
    rawStatus === "cancelled" ||
    rawStatus === "canceled" ||
    order?.buyerDispatchDelayChoice === "cancel"
  ) {
    return "Cancelled";
  }

  const status = getEffectiveRefundAwareStatus(order);
  if (STATUS_LABELS[status]) return STATUS_LABELS[status];

  if (isVaMediationInProgress(order)) return "VA mediation";

  if (
    orderHasActiveRefundCase(order) &&
    order?.buyerReportedIssueAt &&
    !order?.payoutReleased &&
    !order?.userReceivedConfirmed
  ) {
    return STATUS_LABELS["Issue reported"];
  }

  if (confirmationWindowEnded(order)) {
    return "Completed";
  }

  if (isOrderCompleted(order) && String(status).toLowerCase() === "delivered") {
    return "Completed";
  }

  return status || "—";
}

export function isOrderListStatusSuccess(orderOrLabel) {
  const label =
    typeof orderOrLabel === "string"
      ? orderOrLabel
      : getOrderListStatusLabel(orderOrLabel);
  return label === "Completed" || label === "Delivered" || label === "Approved";
}

export function isOrderListStatusResolvedNoRefund(orderOrLabel) {
  const label =
    typeof orderOrLabel === "string"
      ? orderOrLabel
      : getOrderListStatusLabel(orderOrLabel);
  return label === "Resolved (no refund)";
}
